import * as React from 'react';
import { ChevronRight } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { IconBadge } from 'components/ui/icon-badge';

/**
 * Puerto real de `EducationalTipV2` (chirigota, `src/components/MiBeneficioV2/`)
 * — un tip educativo con icono, título, texto y CTA de texto. El mismo
 * componente real se reutiliza con 4 tips distintos de un array (interés
 * compuesto, fiscal, rescate, "company match") — aquí, igual que
 * `MetricCardV2`, cada pantalla lo redefinía como JSX local con el mismo
 * layout y solo el contenido cambiado, en vez de importar una sola pieza.
 */
export interface EducationalTipProps {
	/** Icono (lucide) del círculo. Nunca un emoji: no se pinta igual en cada sistema y no sigue la paleta. */
	icon: React.ComponentType<{ className?: string }>;
	title: string;
	text: string;
	ctaLabel: string;
	onCtaClick?: () => void;
	className?: string;
}

// Siempre sobre el fondo de card (blanco en claro), como el resto de cards: el gris `bg-surface` que
// llevaba hacía que este tip pareciera de otro tipo de componente. Es la variante `elevated`, con borde
// y sombra, para separarse del fondo de página en claro; en oscuro el borde es lo que la hace leerse como superficie.
export const EducationalTip = ({
	icon,
	title,
	text,
	ctaLabel,
	onCtaClick,
	className
}: EducationalTipProps) => (
	<Card variant="elevated" className={className}>
		<CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
			<IconBadge icon={icon} size="lg" shape="circle" className="shrink-0" />
			<div className="flex-1 min-w-0">
				<p className="font-semibold text-foreground">{title}</p>
				<p className="text-sm text-muted-foreground mt-0.5">{text}</p>
			</div>
			<Button variant="ghost" onClick={onCtaClick} className="gap-1 shrink-0">
				{ctaLabel}
				<ChevronRight className="w-4 h-4" />
			</Button>
		</CardContent>
	</Card>
);
